(() => {
  const instances = new WeakMap();

  const initialize = (root) => {
    if (instances.has(root)) return;

    const meter = root.querySelector('[data-cc-reading-progress]');
    const body = root.querySelector('[data-cc-editorial-body]');
    let frame = 0;
    let needsMeasurement = true;
    let destroyed = false;
    let start = 0;
    let distance = 1;
    let lastValue = -1;
    let observer;
    let resizeObserver;

    const updateProgress = () => {
      frame = 0;
      if (destroyed || !meter || !body) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      if (needsMeasurement) {
        const rect = body.getBoundingClientRect();
        start = rect.top + scrollTop - window.innerHeight * 0.2;
        const end = rect.bottom + scrollTop - window.innerHeight * 0.75;
        distance = Math.max(end - start, 1);
        needsMeasurement = false;
      }
      const value = Math.min(100, Math.max(0, ((scrollTop - start) / distance) * 100));

      meter.style.setProperty('--cc-progress', (value / 100).toFixed(4));
      const rounded = Math.round(value);
      if (rounded !== lastValue) {
        meter.setAttribute('aria-valuenow', String(rounded));
        lastValue = rounded;
      }
    };

    const requestProgressUpdate = () => {
      if (destroyed || frame) return;
      frame = window.requestAnimationFrame(updateProgress);
    };

    const requestMeasurement = () => {
      needsMeasurement = true;
      requestProgressUpdate();
    };

    const destroy = () => {
      destroyed = true;
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestProgressUpdate);
      window.removeEventListener('resize', requestMeasurement);
      root.removeEventListener('load', requestMeasurement, true);
      resizeObserver?.disconnect();
      observer?.disconnect();
      instances.delete(root);
    };

    instances.set(root, destroy);
    updateProgress();
    window.addEventListener('scroll', requestProgressUpdate, { passive: true });
    window.addEventListener('resize', requestMeasurement, { passive: true });
    root.addEventListener('load', requestMeasurement, true);
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(requestMeasurement);
      new Set([body, root, document.body]).forEach((element) => {
        if (element) resizeObserver.observe(element);
      });
    }
    document.fonts?.ready.then(requestMeasurement);

    const tocLinks = Array.from(root.querySelectorAll('.cc-editorial-toc a[href^="#"]'));
    const targets = tocLinks
      .map((link) => {
        let id;
        try {
          id = decodeURIComponent(link.getAttribute('href').slice(1));
        } catch {
          return null;
        }
        const target = document.getElementById(id);
        return target ? { link, target } : null;
      })
      .filter(Boolean);

    if (!targets.length || !('IntersectionObserver' in window)) return;

    const setCurrent = (targetId) => {
      targets.forEach(({ link, target }) => {
        if (target.id === targetId) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };

    observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setCurrent(visible[0].target.id);
      },
      { rootMargin: '-16% 0px -68% 0px', threshold: [0, 1] }
    );

    targets.forEach(({ target }) => observer.observe(target));
  };

  const rootsWithin = (scope) => {
    const roots = Array.from(scope.querySelectorAll('[data-cc-editorial-article]'));
    if (scope.matches?.('[data-cc-editorial-article]')) roots.unshift(scope);
    return roots;
  };
  const start = () => rootsWithin(document).forEach(initialize);

  document.addEventListener('shopify:section:load', (event) => rootsWithin(event.target).forEach(initialize));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => instances.get(root)?.());
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
